import axios from "axios";
import useAppDispatch from "utils/hooks/useAppDispatch";
import { setIsAuthenticated, setUserId } from "modules/user/userSlice";
import {
  clearJWTFromStorage,
  jwtHasValidExpiration,
  getCurrentUserToken,
} from "utils/tokens/tokenHelpers";
import { showCustomNotification } from "utils/notifications/notificationHelpers";
import { makeAgHandicapByUserIdMutation } from "./userQueries";
import type { EntitiesState, QueryState } from "redux-query";
import { type RunMutation, useMutation } from "redux-query-react";
import type { AgLockerUpdateBody } from "QueryTypes";
import { LocalStorageItems } from "utils/hooks/localStorage";
import { v4 as uuidv4 } from "uuid";
import { useEffect, useState } from "react";
import { config } from "@/config/env";

const headers = {
  "API-Key": config.apiKey,
  "Content-Type": "application/json",
};

export type LoginUserResult =
  | { success: true }
  | { success: false; error: unknown };

export const loginUser = async (
  username: string,
  password: string,
  rememberMe?: boolean,
): Promise<LoginUserResult> => {
  const data = {
    username,
    password,
  };

  try {
    const res = await axios.post(
      `${config.apiBaseUrl}/api/users/authenticate`,
      data,
      {
        headers,
      },
    );

    if (rememberMe) {
      localStorage.setItem("JWT", res.data);
    } else {
      sessionStorage.setItem("JWT", res.data);
    }

    return { success: true };
  } catch (error: unknown) {
    return { success: false, error };
  }
};

export const logoutUser = (expired?: boolean) => {
  clearJWTFromStorage();
  if (expired) {
    showCustomNotification(
      "Logging out due to inactivity. Please login again.",
    );
  }
  window.location.href = "/";
};

export const logoutIfTokenExpired = (token: string | null): boolean => {
  if (token) {
    if (!jwtHasValidExpiration(token)) {
      logoutUser(true);
      return true;
    }
  }
  return false;
};

export const getUserIdFromToken = (): string => {
  const token = getCurrentUserToken();
  if (!token) return "";

  return token?.userId ?? "";
};

export const useAuthenticateToken = (enabled: boolean) => {
  const dispatch = useAppDispatch();
  const userToken = getCurrentUserToken();

  const [authenticated, setAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled) return;

    const hasJWT = localStorage.getItem("JWT") || sessionStorage.getItem("JWT");

    if (hasJWT && userToken) {
      const userId = userToken.userId;
      dispatch(setIsAuthenticated({ isAuthenticated: true }));
      dispatch(setUserId({ userId }));

      const browserId = localStorage.getItem(LocalStorageItems.BROWSERID);
      if (!browserId) {
        const newBrowserId = uuidv4();
        localStorage.setItem(LocalStorageItems.BROWSERID, newBrowserId);
        window.browserId = newBrowserId;
      }
      setAuthenticated(true);
    } else {
      dispatch(setIsAuthenticated({ isAuthenticated: false }));
      setAuthenticated(false);
    }
  }, [enabled, dispatch, userToken]);

  return authenticated;

  /*if (enabled) {
    if (localStorage.getItem("JWT") || sessionStorage.getItem("JWT")) {
      dispatch(setIsAuthenticated({ isAuthenticated: true }));
      dispatch(setUserId({ userId: userId }));
      if (!browserId) {
        localStorage.setItem(LocalStorageItems.BROWSERID, uuidv4());
        window.browserId = localStorage.getItem(LocalStorageItems.BROWSERID);
      }

      return true;
    } else {
      dispatch(setIsAuthenticated({ isAuthenticated: false }));
      return false;
    }
  }*/
};

export const useUpdateUserAgHandicap = (
  userId: string,
  aGHandicapValue: any,
): [QueryState, RunMutation<EntitiesState, [AgLockerUpdateBody]>] => {
  const [updateAgHandicapByUserIdQueryState, updateAgHandicap] = useMutation(
    () => makeAgHandicapByUserIdMutation(userId, aGHandicapValue),
  );

  return [updateAgHandicapByUserIdQueryState, updateAgHandicap];
};
